import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Calendar, Phone, User, Building, DollarSign } from 'lucide-react';
import { referralService } from '../api/referrals';
import type { Referral } from '../types';
import toast from 'react-hot-toast';

export default function ReferralView() {
  const { formatCurrency } = useCurrency();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [referral, setReferral] = useState<Referral | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadReferral(id);
    }
  }, [id]);

  const loadReferral = async (referralId: string) => {
    try {
      setLoading(true);
      const data = await referralService.getById(referralId);
      setReferral(data);
    } catch (error) {
      console.error('Failed to load referral:', error);
      toast.error('Failed to load referral');
      navigate('/referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!referral) return;
    
    try {
      await referralService.updateStatus(referral.id, newStatus as any);
      setReferral({ ...referral, status: newStatus as any });
      toast.success('Status updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const getReferrerBadge = (kind: string) => {
    const styles = {
      external_partner: 'bg-purple-100 text-purple-800',
      internal_staff: 'bg-blue-100 text-blue-800',
      stakeholder: 'bg-green-100 text-green-800',
    };
    return styles[kind as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!referral) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Referral not found</h3>
        <p className="text-gray-500">The referral you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/referrals')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referral Details</h1>
            <p className="text-gray-600">View and manage referral information</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(referral.status)}`}>
            {referral.status}
          </span>
          <button
            onClick={() => navigate(`/referrals/${referral.id}/edit`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Patient Name</div>
                  <div className="font-medium text-gray-900">
                    {referral.patientName || 'Unknown Patient'}
                  </div>
                </div>
              </div>
              {referral.patientPhone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <div className="font-medium text-gray-900">{referral.patientPhone}</div>
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Date Created</div>
                  <div className="font-medium text-gray-900">
                    {new Date(referral.dateCreated).toLocaleDateString()}
                  </div>
                </div>
              </div>
              {referral.estimatedAmount && (
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Estimated Amount</div>
                    <div className="font-medium text-gray-900">
                      ${referral.estimatedAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Referral Details</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Type</div>
                <div className="font-medium text-gray-900 capitalize">
                  {referral.type}-Referral
                </div>
              </div>
              {referral.toSpecialty && (
                <div>
                  <div className="text-sm text-gray-500">Specialty</div>
                  <div className="font-medium text-gray-900">{referral.toSpecialty}</div>
                </div>
              )}
              {referral.reason && (
                <div>
                  <div className="text-sm text-gray-500">Reason</div>
                  <div className="font-medium text-gray-900">{referral.reason}</div>
                </div>
              )}
              {referral.notes && (
                <div>
                  <div className="text-sm text-gray-500">Notes</div>
                  <div className="font-medium text-gray-900">{referral.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Referral Source</h2>
            <div className="space-y-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getReferrerBadge(referral.source.kind)}`}>
                {referral.source.kind.replace('_', ' ')}
              </span>
              {referral.source.code && (
                <div>
                  <div className="text-sm text-gray-500">Code</div>
                  <div className="font-medium text-gray-900">{referral.source.code}</div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Status Management</h2>
            <div className="space-y-3">
              <button
                onClick={() => handleStatusUpdate('scheduled')}
                disabled={referral.status === 'scheduled'}
                className="w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark as Scheduled
              </button>
              <button
                onClick={() => handleStatusUpdate('completed')}
                disabled={referral.status === 'completed'}
                className="w-full px-4 py-2 text-left text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark as Completed
              </button>
              <button
                onClick={() => handleStatusUpdate('cancelled')}
                disabled={referral.status === 'cancelled'}
                className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark as Cancelled
              </button>
            </div>
          </div>

          {referral.type === 'out' && referral.toPartnerId && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Referred To</h2>
              <div className="flex items-center space-x-3">
                <Building className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Partner</div>
                  <div className="font-medium text-gray-900">{referral.toPartnerId}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
